import { notifyAgentRequestContentChangedFromAgent } from './agentRequestContentEvents';
import { notifyAgentAssertionsChangedFromAgent } from './agentAssertionsEvents';

export const ENVIRONMENT_VARIABLES_CHANGED_EVENT = 'environment-variables-changed';
export const ENVIRONMENTS_CHANGED_EVENT = 'environments-changed';

export type EnvironmentVariablesChangedDetail = {
  environmentId: number;
  projectId?: number;
  source?: string;
};

export type EnvironmentsChangedDetail = {
  projectId: number;
  environmentId?: number;
  source?: string;
};

function parseOptionalId(value: unknown): number | undefined {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
}

export function dispatchEnvironmentVariablesChanged(
  detail: EnvironmentVariablesChangedDetail,
): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(ENVIRONMENT_VARIABLES_CHANGED_EVENT, { detail }));
}

export function dispatchEnvironmentsChanged(detail: EnvironmentsChangedDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(ENVIRONMENTS_CHANGED_EVENT, { detail }));
}

const ENV_VAR_MUTATING_AGENT_ACTIONS = new Set([
  'add_environment_variable',
  'store_credentials',
  'apply_environment_variables',
  'apply_credential_placeholders',
  'execute_login',
]);

const ENV_LIST_MUTATING_AGENT_ACTIONS = new Set([
  'create_environment',
  'create_project_import',
]);

/** Notify Environments page (and other listeners) after agent actions that mutate env variables. */
export function notifyEnvironmentVariablesChangedFromAgent(
  action: string | undefined,
  payload?: Record<string, unknown> | null,
  session?: { environmentId?: number | null; projectId?: number | null } | null,
): void {
  if (!action || !ENV_VAR_MUTATING_AGENT_ACTIONS.has(action)) return;
  const environmentId =
    parseOptionalId(payload?.environmentId) ?? parseOptionalId(session?.environmentId);
  if (environmentId == null) return;
  dispatchEnvironmentVariablesChanged({
    environmentId,
    projectId: parseOptionalId(payload?.projectId) ?? parseOptionalId(session?.projectId),
    source: 'agent',
  });
}

/** Notify Environments page after agent actions that create/change environments. */
export function notifyEnvironmentsChangedFromAgent(
  action: string | undefined,
  payload?: Record<string, unknown> | null,
  session?: { environmentId?: number | null; projectId?: number | null } | null,
): void {
  if (!action || !ENV_LIST_MUTATING_AGENT_ACTIONS.has(action)) return;
  const projectId =
    parseOptionalId(payload?.projectId) ?? parseOptionalId(session?.projectId);
  if (projectId == null) return;
  dispatchEnvironmentsChanged({
    projectId,
    environmentId:
      parseOptionalId(session?.environmentId) ?? parseOptionalId(payload?.environmentId),
    source: 'agent',
  });
}

/**
 * Dispatch both list + variable refresh events for any agent mutation that affects Environments.
 * Safe to call for any action; no-ops when the action does not mutate environments/vars.
 */
export function notifyEnvironmentUiFromAgent(
  action: string | undefined,
  payload?: Record<string, unknown> | null,
  session?: { environmentId?: number | null; projectId?: number | null } | null,
): void {
  notifyEnvironmentsChangedFromAgent(action, payload, session);
  notifyEnvironmentVariablesChangedFromAgent(action, payload, session);
}

/**
 * Notify env UI + Collections request editor after agent mutations.
 * Safe for any action; each helper no-ops when unrelated.
 */
export function notifyAgentSideEffectsFromAgent(
  action: string | undefined,
  payload?: Record<string, unknown> | null,
  session?: { environmentId?: number | null; projectId?: number | null } | null,
): void {
  notifyEnvironmentUiFromAgent(action, payload, session);
  notifyAgentRequestContentChangedFromAgent(action, payload, session);
  notifyAgentAssertionsChangedFromAgent(action, payload, session);
}
